import {ElementRef, Injectable, OnDestroy} from '@angular/core';
import {ShContextMenuComponent} from './sh-context-menu.component';
import {CloseScrollStrategy, Overlay} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {ConnectedPositionStrategy} from '@angular/cdk/overlay/typings/position/connected-position-strategy';
import {ShContextMenuEvent, ShContextSubMenuEvent} from './sh-context-menu.models';
import {OverlayRef} from '@angular/cdk/overlay';
import {fromEvent} from 'rxjs';
import {Subscription} from 'rxjs';
export class ShContextMenuService implements OnDestroy {
  activeOverlays: OverlayRef[] = [];
  backDropSub: Subscription;

  activeMenu: ShContextMenuComponent;
/**
 * @param {?} overlay
 */
constructor(private overlay: Overlay) {
  }
/**
 * @param {?} ctxEvent
 * @return {?}
 */
openMenu(ctxEvent: ShContextMenuEvent) {
    this.closeCurrentOverlays();
    const {menu, mouseEvent, targetElement, data} = ctxEvent;

    this.activeMenu = menu;

    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    this.overrideGetBoundingClientRect(targetElement, mouseEvent);

    const /** @type {?} */ scrollStrategy = this.buildCloseScrollStrategy();
    const /** @type {?} */ positionStrategy = this.buildConnectedPositionStrategy(targetElement);

    this.attachContextToItems(menu, data);

    const /** @type {?} */ overlayRef = this.createAndAttachOverlay(positionStrategy, scrollStrategy, menu, true);
    this.attachOverlayRef(menu, overlayRef);

    this.registerBackdropEvents(overlayRef);
  }
/**
 * @param {?} ctxEvent
 * @return {?}
 */
openSubMenu(ctxEvent: ShContextSubMenuEvent): any {
    const {menu, mouseEvent, targetElement, data, parentMenu} = ctxEvent;

    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    const /** @type {?} */ scrollStrategy = this.buildCloseScrollStrategy();
    const /** @type {?} */ positionStrategy = this.buildConnectedPositionStrategyForSubMenu(targetElement);
    const /** @type {?} */ overlayRef = this.createAndAttachOverlay(positionStrategy, scrollStrategy, menu, false);

    this.attachContextToItems(menu, data);
    this.attachThisContext(menu, parentMenu);
    this.attachOverlayRef(menu, overlayRef);
  }
/**
 * @return {?}
 */
destroy() {
    this.closeCurrentOverlays();
    this.backDropSub.unsubscribe();
  }
/**
 * @return {?}
 */
ngOnDestroy(): void {
    this.destroy();
  }
/**
 * @param {?} menu
 * @return {?}
 */
closeSubMenus(menu: ShContextMenuComponent) {
    const /** @type {?} */ itemsWithSubMenus = menu
      .menuItems
      .filter(i => !!i.subMenu && !!i.subMenu.overlayRef);

    if (itemsWithSubMenus.length) {
      itemsWithSubMenus.forEach(sm => this.closeSubMenus(sm.subMenu));

      const /** @type {?} */ overlayRefs = itemsWithSubMenus
        .map(i => i.subMenu.overlayRef);

      overlayRefs.forEach(r => r.dispose());
    }
  }
/**
 * @param {?} overlayRef
 * @return {?}
 */
private registerBackdropEvents(overlayRef: OverlayRef) {
    const /** @type {?} */ elm = overlayRef.backdropElement;

    this.backDropSub = fromEvent(elm, 'mousedown')
      .subscribe(this.closeCurrentOverlays.bind(this));
  }
/**
 * @param {?} positionStrategy
 * @param {?} scrollStrategy
 * @param {?} menu
 * @param {?=} hasBackdrop
 * @return {?}
 */
private createAndAttachOverlay(positionStrategy: ConnectedPositionStrategy,
                                 scrollStrategy: CloseScrollStrategy,
                                 menu: ShContextMenuComponent,
                                 hasBackdrop: boolean = true) {

    const /** @type {?} */ overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: hasBackdrop,
      backdropClass: 'sh-backdrop'
    });

    /*
     TODO: try passing the TemplatePortal context (data)
     and then injecting it to the *ngTemplateOutlet in the component template
    */
    const /** @type {?} */ menuPortal = new TemplatePortal(menu.menuTemplate, menu.menuContainer);
    overlayRef.attach(menuPortal);

    this.activeOverlays.push(overlayRef);

    return overlayRef;
  }
/**
 * @return {?}
 */
private buildCloseScrollStrategy() {
    return this.overlay.scrollStrategies.close();
  }
/**
 * @param {?} elm
 * @return {?}
 */
private buildConnectedPositionStrategy(elm: ElementRef): ConnectedPositionStrategy {
    return this
      .overlay
      .position()
      .connectedTo(elm,
        {originX: 'start', originY: 'bottom'},
        {overlayX: 'start', overlayY: 'top'})
      .withFallbackPosition(
        {originX: 'start', originY: 'top'},
        {overlayX: 'start', overlayY: 'bottom'})
      .withFallbackPosition(
        {originX: 'end', originY: 'top'},
        {overlayX: 'start', overlayY: 'top'})
      .withFallbackPosition(
        {originX: 'start', originY: 'top'},
        {overlayX: 'end', overlayY: 'top'})
      .withFallbackPosition(
        {originX: 'end', originY: 'center'},
        {overlayX: 'start', overlayY: 'center'})
      .withFallbackPosition(
        {originX: 'start', originY: 'center'},
        {overlayX: 'end', overlayY: 'center'});
  }
/**
 * @param {?} elm
 * @return {?}
 */
private buildConnectedPositionStrategyForSubMenu(elm: ElementRef): ConnectedPositionStrategy {
    return this
      .overlay
      .position()
      .connectedTo(elm,
        {originX: 'end', originY: 'top'},
        {overlayX: 'start', overlayY: 'top'})
      .withFallbackPosition(
        {originX: 'start', originY: 'top'},
        {overlayX: 'end', overlayY: 'top'})
      .withFallbackPosition(
        {originX: 'end', originY: 'bottom'},
        {overlayX: 'start', overlayY: 'bottom'})
      .withFallbackPosition(
        {originX: 'start', originY: 'bottom'},
        {overlayX: 'end', overlayY: 'bottom'});
  }
/**
 * @param {?} elm
 * @param {?} event
 * @return {?}
 */
private overrideGetBoundingClientRect(elm: ElementRef, event: MouseEvent) {
    const {clientX, clientY} = event;

    elm.nativeElement.getBoundingClientRect = (): ClientRect => {
      return {
        bottom: clientY,
        height: 0,
        left: clientX,
        right: clientX,
        top: clientY,
        width: 0
      };
    };
  }
/**
 * @return {?}
 */
private closeCurrentOverlays() {
    this.activeOverlays.forEach((o) => {
      o.detach();
      o.dispose();
    });

    this.activeOverlays = [];

    // TODO: create close subject and emit.
    // subscribe in component
    if (this.activeMenu) {
      this.activeMenu.close();
    }
  }
/**
 * @param {?} menu
 * @param {?} data
 * @return {?}
 */
private attachContextToItems(menu: ShContextMenuComponent, data: any) {
    menu.menuItems.forEach(i => i.context.$implicit = data);
  }
/**
 * @param {?} menu
 * @param {?} parentMenu
 * @return {?}
 */
private attachThisContext(menu: ShContextMenuComponent, parentMenu: ShContextMenuComponent) {
    menu.thisContext = parentMenu.thisContext;
  }
/**
 * @param {?} menu
 * @param {?} overlayRef
 * @return {?}
 */
private attachOverlayRef(menu: ShContextMenuComponent, overlayRef: OverlayRef) {
    menu.overlayRef = overlayRef;
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Overlay, },
];
}

function ShContextMenuService_tsickle_Closure_declarations() {
/** @type {?} */
ShContextMenuService.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ShContextMenuService.ctorParameters;
/** @type {?} */
ShContextMenuService.prototype.activeOverlays;
/** @type {?} */
ShContextMenuService.prototype.backDropSub;
/** @type {?} */
ShContextMenuService.prototype.activeMenu;
/** @type {?} */
ShContextMenuService.prototype.overlay;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
